import { ReactNode } from 'react';
import { useAdminConfig } from '../../hooks/useAdminConfig';
import type { AdminPermission } from '../../config/admin-permissions';

type ModuleKey = 'dashboard' | 'users' | 'export' | 'forecast';
type PremiumModuleKey = ModuleKey | 'marketing' | 'promocodes';

const moduleTitle: Record<PremiumModuleKey, string> = {
  dashboard: 'Дашборд',
  users: 'Пользователи',
  export: 'Экспорт',
  forecast: 'Прогноз',
  marketing: 'Источники',
  promocodes: 'Промокоды',
};

const modulePromo: Record<PremiumModuleKey, string> = {
  dashboard:
    'Финансовая аналитика: выручка, прибыль, маржа, конверсии, retention и динамика по периодам.',
  users:
    'Сегментация и поиск клиентов, история активности, источники привлечения и работа с базой.',
  export:
    'Экспорт клиентской и финансовой отчётности в CSV/XLSX для рекламы, бухгалтерии и CRM.',
  forecast:
    'Прогноз выручки и спроса, чтобы планировать закупки, рекламные бюджеты и персонал.',
  marketing:
    'Генерация рекламных ссылок по источникам (start-параметры) и атрибуция каналов в отчётах.',
  promocodes:
    'Создание и управление промокодами: активация, лимиты, даты действия и контроль применений.',
};

export default function AdminAccessGate({
  moduleKey,
  requireAccess,
  requirePermission,
  children,
}: {
  moduleKey?: PremiumModuleKey;
  requireAccess?: 'catalog' | 'main';
  requirePermission?: AdminPermission;
  children: ReactNode;
}) {
  const { config, loading, error, reload } = useAdminConfig();
  if (loading) {
    return <div className="admin-card">Проверка доступа...</div>;
  }

  if (!config) {
    return (
      <div className="admin-card admin-module-locked">
        <h2 className="admin-card-title">Не удалось загрузить настройки админ-панели</h2>
        <p>{error || 'Сервер не вернул конфигурацию доступа. Проверьте backend/.env и доступ к /api/admin/config.'}</p>
        <button type="button" className="admin-btn admin-btn-secondary" onClick={reload}>
          Повторить
        </button>
      </div>
    );
  }

  if (requireAccess && config?.access?.[requireAccess] === false) {
    return (
      <div className="admin-card admin-module-locked">
        <h2 className="admin-card-title">Доступ ограничен</h2>
        <p>
          У вас нет доступа к разделу <strong>{requireAccess === 'catalog' ? 'Каталог' : 'Дашборд и аналитика'}</strong>.
        </p>
      </div>
    );
  }
  if (requirePermission && !(config.permissions ?? []).includes(requirePermission)) {
    return (
      <div className="admin-card admin-module-locked">
        <h2 className="admin-card-title">Недостаточно прав</h2>
        <p>У вас нет permission для этого раздела: <strong>{requirePermission}</strong>.</p>
      </div>
    );
  }

  if (!moduleKey) {
    return <>{children}</>;
  }

  if (!config?.modules?.[moduleKey]) {
    return (
      <div className="admin-card admin-module-locked">
        <h2 className="admin-card-title">Модуль недоступен</h2>
        <p>
          Раздел <strong>{moduleTitle[moduleKey]}</strong> отключен.
        </p>
        <p>{modulePromo[moduleKey]}</p>
        <p>Для подключения этого раздела активируйте расширенный пакет админ-панели.</p>
      </div>
    );
  }

  return <>{children}</>;
}
